/**
 * USDC.e (bridged USDC) ERC-20 allowance helper for Polymarket on Polygon.
 *
 * Polymarket settles in USDC.e — the bridged USDC from Ethereum mainnet, NOT
 * native USDC on Polygon. The user must approve the relevant exchange
 * contract(s) to spend USDC.e before any BUY can fill.
 *
 * Spenders the user needs to approve (max-uint256, once per wallet):
 *   - CTF Exchange — used by all standard markets
 *   - Neg-Risk CTF Exchange — used by neg-risk markets
 *   - Neg-Risk Adapter — used internally by neg-risk for splits/conversions
 *
 * The function below approves only the spenders required for the order's
 * specific market type. We always BUY in this app, so CTF (ConditionalTokens)
 * approvals for SELL aren't wired here.
 */

import {
  encodeFunctionData,
  erc20Abi,
  maxUint256,
  type Address,
  type Hex,
  type PublicClient,
  type WalletClient,
} from "viem";
import {
  CTF_EXCHANGE_ADDRESS,
  NEG_RISK_CTF_EXCHANGE_ADDRESS,
} from "./signing";

/** USDC.e (bridged USDC) on Polygon. NOT native USDC. */
export const USDC_E_ADDRESS =
  "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174" as const;

/**
 * NegRiskAdapter — internal contract used to split/merge neg-risk positions.
 * Required as a USDC spender for neg-risk markets in addition to the exchange.
 */
export const NEG_RISK_ADAPTER_ADDRESS =
  "0xd91E80cF2E7be2e162c6513ceD06f1dD0dA35296" as const;

/** Spenders that need USDC.e allowance for a given market type. */
export function spendersForBuy(negRisk: boolean): readonly Address[] {
  return negRisk
    ? [NEG_RISK_CTF_EXCHANGE_ADDRESS, NEG_RISK_ADAPTER_ADDRESS]
    : [CTF_EXCHANGE_ADDRESS];
}

/** Read the user's current USDC.e allowance for a single spender. */
export async function readUsdcAllowance(
  publicClient: PublicClient,
  owner: Address,
  spender: Address,
): Promise<bigint> {
  const v = await publicClient.readContract({
    address: USDC_E_ADDRESS,
    abi: erc20Abi,
    functionName: "allowance",
    args: [owner, spender],
  });
  return v as bigint;
}

/**
 * Send USDC.e.approve(spender, max) and return the tx hash. Caller is
 * responsible for waiting on confirmation via publicClient.waitForTransactionReceipt.
 */
export async function approveUsdcMax(
  walletClient: WalletClient,
  owner: Address,
  spender: Address,
): Promise<Hex> {
  const data = encodeFunctionData({
    abi: erc20Abi,
    functionName: "approve",
    args: [spender, maxUint256],
  });
  const hash = await walletClient.sendTransaction({
    account: owner,
    to: USDC_E_ADDRESS,
    data,
    chain: null,
  });
  return hash;
}

/**
 * Result describing what allowance work happened (or was already in place).
 *
 * `txHashes` is empty when every spender was already approved at >= required.
 */
export type EnsureAllowancesResult = {
  approved: boolean;
  txHashes: Hex[];
  /** Spenders we decided still need allowance (for diagnostics). */
  pending: Address[];
};

/**
 * Make sure the user has enough USDC.e allowance to BUY the given USDC amount
 * across the spenders implied by `negRisk`. Sends max-uint256 approvals for
 * any short spender, waiting for each to confirm before returning.
 *
 * `requiredAmount` is in USDC.e base units (6 decimals).
 */
export async function ensureBuyAllowances(
  publicClient: PublicClient,
  walletClient: WalletClient,
  owner: Address,
  negRisk: boolean,
  requiredAmount: bigint,
): Promise<EnsureAllowancesResult> {
  const spenders = spendersForBuy(negRisk);
  const txHashes: Hex[] = [];
  const pending: Address[] = [];

  for (const spender of spenders) {
    const current = await readUsdcAllowance(publicClient, owner, spender);
    if (current >= requiredAmount) continue;
    pending.push(spender);
    const hash = await approveUsdcMax(walletClient, owner, spender);
    await publicClient.waitForTransactionReceipt({ hash });
    txHashes.push(hash);
  }

  return { approved: true, txHashes, pending };
}
